import cx from 'classnames';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { format } from 'domains/dates/format';
import { AnimatedCollapse } from 'components/AnimatedCollapse';
import { CardSection } from 'components/Card';
import { Divider } from 'components/Divider';
import { Icon } from 'components/Icon';
import { Heading, Paragraph } from 'components/Typography';
import { Goal, ShortTermGoal } from 'domains/care-plans/types';
import { DATE_SHORT_MONTH } from 'utils/date';
import { HelpText } from 'components/HelpText';
import { formatGoalTarget } from 'domains/goals/goals';
import { GoalStats } from './GoalStats';
import { GoalStatusToken } from './GoalStatusToken';

export type Props = {
  goal: Goal;
  defaultOpen?: boolean;

  /** Use this when rendering inside a PDF */
  pdf?: boolean;
};

type SectionProps = Props & {
  shortTermGoalComponent: React.ComponentType<{ shortTermGoal: ShortTermGoal; showDivider: boolean; pdf?: boolean }>;
};

export const GoalSection = ({
  goal: {
    type,
    goalBaselineDate,
    goalBaseline,
    goalBaselineContext,
    goalContext,
    goalDescription,
    goalDueDate,
    goalStatus,
    goalTarget,
    shortTermGoals,
  },
  defaultOpen,
  pdf,
  shortTermGoalComponent: ShortTermGoalSection,
}: SectionProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      {!pdf && <Divider className="my-24 md:my-32" />}

      <CardSection
        className={cx('font-medium', {
          'px-2 py-2 break-after-page': pdf,
        })}
      >
        <AnimatedCollapse defaultOpen={defaultOpen}>
          {({ isOpen }) => (
            <>
              <div
                className={cx('grid text-left gap-x-24', {
                  'grid-cols-[40px_1fr]': isOpen,
                  'grid-cols-[40px_1fr] lg:grid-cols-[40px_1fr_minmax(210px,_min-content)]': !isOpen,
                })}
              >
                <AnimatedCollapse.Trigger className="col-span-2 flex gap-x-24">
                  <div
                    className={cx(
                      'bg-primary-200 rounded-full text-black-100% border border-black-12% shrink-0 transition-transform h-max',
                      {
                        '-rotate-90 duration-300': !isOpen,
                        'border-transparent shadow-solid shadow-black-100% bg-purple-300': isOpen,
                      },
                    )}
                  >
                    <div className="w-40 aspect-w-1 aspect-h-1">
                      <div
                        data-testid="goal-section-minus-plus"
                        className="flex items-center justify-center w-full h-full"
                      >
                        <Icon name={isOpen ? 'Minus' : 'Plus'} />
                      </div>
                    </div>
                  </div>

                  <div
                    data-testid="goal-section-evaluationReport-goals-longTermGoalHeading"
                    className="flex flex-col flex-wrap sm:flex-row items-start sm:items-center gap-8 sm:h-full"
                  >
                    <Heading level="h4" as="h4" className="shrink-0">
                      {t('evaluationReport.goals.longTermGoalHeading')}
                    </Heading>
                    <GoalStatusToken status={goalStatus} />
                  </div>
                </AnimatedCollapse.Trigger>

                <div
                  data-testid="goal-section-evaluationReport-goals-goalTargetLabel"
                  className={cx(
                    'inline-block lg-d:row-start-3 lg-d:col-start-2 lg:row-span-3 sm:w-max sm:min-w-card lg-d:mt-8',
                    {
                      hidden: isOpen,
                    },
                  )}
                >
                  <GoalStats
                    pdf
                    className="transition-opacity"
                    stats={[
                      {
                        eyebrowLabel: t('evaluationReport.goals.goalTargetLabel', {
                          date: goalDueDate ? format(parseISO(goalDueDate), DATE_SHORT_MONTH) : '',
                        }),
                        heading: formatGoalTarget(goalTarget, type),
                        label: goalContext,
                      },
                    ]}
                  />
                </div>

                <div className="flex flex-col items-center row-span-3 pt-8">
                  <div
                    className={cx('border-l-[3px] border-purple-200 grow transition-opacity opacity-0', {
                      'opacity-100 duration-500': isOpen,
                      'border-transparent': pdf,
                    })}
                  />
                </div>
                <div data-testid="goal-section-goal-description">
                  <Paragraph className="mt-8 md:mt-16 pb-12">{goalDescription}</Paragraph>
                </div>
              </div>

              <AnimatedCollapse.Contents>
                <div className="grid grid-cols-[40px_1fr] gap-x-24 break-after-all">
                  <div className="flex flex-col items-center row-span-3 pb-8">
                    <div
                      className={cx('border-l-[3px] border-purple-200 grow', {
                        'border-transparent': pdf,
                      })}
                    />
                  </div>

                  <div data-testid="goal-section-evaluationReport-goal-stats" className="pb-32">
                    {!pdf && (
                      <HelpText
                        text={t('evaluationReport.goals.whatIsLongTermGoal')}
                        tooltipText={t('evaluationReport.goals.longTermGoalExplanation')}
                        className="mb-32 mt-16 underline font-semibold"
                      />
                    )}
                    <GoalStats
                      pdf
                      className="mt-14"
                      stats={[
                        {
                          eyebrowLabel: t('evaluationReport.goals.baselineLabel', {
                            date: goalBaselineDate ? format(parseISO(goalBaselineDate), DATE_SHORT_MONTH) : '',
                          }),
                          heading: formatGoalTarget(goalBaseline, type),
                          label: goalBaselineContext,
                        },
                        {
                          eyebrowLabel: t('evaluationReport.goals.goalTargetLabel', {
                            date: goalDueDate ? format(parseISO(goalDueDate), DATE_SHORT_MONTH) : '',
                          }),
                          heading: formatGoalTarget(goalTarget, type),
                          label: goalContext,
                        },
                      ]}
                    />
                  </div>
                </div>

                {shortTermGoals.map((shortTermGoal, i) => (
                  <ShortTermGoalSection
                     
                    key={`${shortTermGoal.goalDescription}-${i}`}
                    showDivider={i < shortTermGoals.length - 1}
                    shortTermGoal={shortTermGoal}
                    pdf={pdf}
                  />
                ))}
              </AnimatedCollapse.Contents>
            </>
          )}
        </AnimatedCollapse>
      </CardSection>
    </>
  );
};
